import React from "react";
import ReactEcharts from "echarts-for-react";
import { postureIconChipStyle, postureIconStyle } from "./iconStyles";

const ORANGE_COLORS = {
  primary: '#FF5B00',
  dark: '#e85a28',
  darker: '#d14d1f',
  light: '#ff8c61',
  lighter: '#ffb399',
};

const MonthlyThreadChart = ({ analyticsData = [] }) => {
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  
  // Detect dark mode
  const isDarkMode = document.documentElement.getAttribute('data-layout-mode') === 'dark';
  const textColor = isDarkMode ? '#a6b0cf' : '#858d98';
  const splitLineColor = isDarkMode ? 'rgba(166, 176, 207, 0.1)' : 'rgba(133, 141, 152, 0.1)';
  
  if (!analyticsData || analyticsData.length === 0) {
    return (
      <div className="text-center py-5">
        <span className="d-inline-flex align-items-center justify-content-center mb-3" style={postureIconChipStyle("var(--vz-primary)")}>
          <i className="ri-line-chart-line" style={postureIconStyle("var(--vz-primary)")}></i>
        </span>
        <p className="text-muted">No threat trend data available</p>
      </div>
    );
  }
  
  // Extract data from analyticsData
  const categories = analyticsData.map(item => monthNames[item.month - 1]);
  const totalThreats = analyticsData.map(item => item.total_threats);
  const criticalThreats = analyticsData.map(item => item.critical_threats);
  const resolvedThreats = analyticsData.map(item => item.resolved_threats);
  
  const option = {
    title: { 
      text: `Threat Trends (Monthly View)`, 
      left: 'center', 
      textStyle: { 
        color: ORANGE_COLORS.primary, 
        fontSize: 14,
        fontWeight: 600
      } 
    },
    tooltip: { 
      trigger: 'axis', 
      axisPointer: { 
        type: 'cross',
        label: {
          backgroundColor: ORANGE_COLORS.primary
        }
      },
      backgroundColor: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : 'rgba(255, 255, 255, 0.95)',
      borderColor: isDarkMode ? '#3e4c5f' : ORANGE_COLORS.lighter,
      borderWidth: 1,
      textStyle: { color: textColor, fontSize: 12 },
      formatter: params => {
        let result = `<strong>${params[0].axisValue}</strong><br/>`;
        params.forEach(param => {
          result += `
<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background-color:${param.color};margin-right:5px;"></span>
${param.seriesName}: <strong>${param.value}</strong><br/>`;
        });
        return result;
      }
    },
    legend: { 
      data: ['Total Threats', 'Critical Threats', 'Resolved Threats'], 
      top: 35, 
      textStyle: { color: textColor, fontSize: 11 },
      itemGap: 15
    },
    grid: { 
      left: '5%', 
      right: '5%', 
      bottom: '8%', 
      top: '22%', 
      containLabel: true 
    },
    xAxis: { 
      type: 'category', 
      data: categories, 
      axisLine: { 
        lineStyle: { color: isDarkMode ? 'rgba(166, 176, 207, 0.2)' : 'rgba(133, 141, 152, 0.2)' } 
      }, 
      axisLabel: { 
        color: textColor,
        fontSize: 11
      },
      axisTick: {
        show: false
      }
    },
    yAxis: { 
      type: 'value', 
      axisLine: { 
        show: false
      }, 
      axisLabel: { 
        color: textColor,
        fontSize: 11
      }, 
      splitLine: { 
        lineStyle: { 
          color: splitLineColor,
          type: 'dashed'
        } 
      }
    },
    series: [
      { 
        name: 'Total Threats', 
        data: totalThreats, 
        type: 'line', 
        smooth: true, 
        symbol: 'circle', 
        symbolSize: 7, 
        lineStyle: { 
          color: ORANGE_COLORS.primary,
          width: 3
        }, 
        itemStyle: { 
          color: ORANGE_COLORS.primary,
          borderWidth: 2,
          borderColor: '#fff'
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: `${ORANGE_COLORS.primary}40` },
              { offset: 1, color: `${ORANGE_COLORS.primary}05` }
            ]
          }
        },
        emphasis: {
          focus: 'series',
          itemStyle: {
            borderWidth: 3,
            shadowBlur: 10,
            shadowColor: ORANGE_COLORS.primary
          }
        }
      },
      { 
        name: 'Critical Threats', 
        data: criticalThreats, 
        type: 'line', 
        smooth: true, 
        symbol: 'circle', 
        symbolSize: 7, 
        lineStyle: { 
          color: '#dc3545',
          width: 3
        }, 
        itemStyle: { 
          color: '#dc3545',
          borderWidth: 2,
          borderColor: '#fff'
        },
        emphasis: {
          focus: 'series',
          itemStyle: {
            borderWidth: 3,
            shadowBlur: 10,
            shadowColor: '#dc3545'
          }
        }
      },
      { 
        name: 'Resolved Threats', 
        data: resolvedThreats, 
        type: 'line', 
        smooth: true, 
        symbol: 'circle', 
        symbolSize: 7, 
        lineStyle: { 
          color: '#198754',
          width: 3
        }, 
        itemStyle: { 
          color: '#198754',
          borderWidth: 2,
          borderColor: '#fff'
        },
        emphasis: {
          focus: 'series',
          itemStyle: {
            borderWidth: 3,
            shadowBlur: 10,
            shadowColor: '#198754'
          }
        }
      }
    ],
    textStyle: { fontFamily: 'Poppins, sans-serif' }
  };
  
  return <ReactEcharts style={{ height: "300px" }} option={option} />;
};

export { MonthlyThreadChart };
